import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  IPaymentGridVo,
  IProfitContentData,
  IResProfitChartList,
  IResProfitDetailInfo,
  ProfitTabType,
} from "./ProfitVo";
import { createAppThunk, type IRes, type IResExcelData } from "store/modules/common";
import Http from "commons/Http";
import moment from "moment";

// 이름 설정
const name = "profit";

export interface IProfit {
  activeTab?: ProfitTabType;
  profitInfo?: IProfitContentData;
  profitDate?: { year: number; month?: number };
  chart: {
    date?: Array<string>;
    revenue?: Array<number>;
  };
  isLoading: {
    chart: boolean;
    profit_list: boolean;
  };
}

function StringToDateCal(date: string, cal: number) {
  // 입력된 날짜를 Date 객체로 변환
  const currentDate = new Date(date);
  currentDate.setMonth(currentDate.getMonth() + cal);
  const text = currentDate.toISOString().slice(0, 7);
  return text;
}
export function toSort(o?: { [key: string]: string }) {
  if (o) {
    const key = Object.keys(o);
    if (key.length) {
      return `${key[0]},${o[key[0]].toLowerCase()}`;
    }
  }
  return undefined;
}

/**
 * 수익 - 지급 그리드 데이터 조회
 */
export const apiGpaymentGridData = createAppThunk<IPaymentGridVo[], { page: number }>(
  `${name}/apiGpaymentGridData`,
  async (option: { page: number }, thunkApi) => {
    const param = {
      year: thunkApi.getState().ui.selectbox?.["payment_year_date"],
    };

    const res = await Http.get<IRes<IPaymentGridVo[]>>("revenue/payment", {
      params: param,
    });

    if (res.data.content) {
      thunkApi.dispatch(rdxSetPaymentGridlList(res.data.content));
    }

    return res.data;
  },
);

/**
 * 수익 ver2- 콘텐츠, 음원/영상, 파트너스 그리드 데이터 조회
 */
export const apiGprofitDetailList = createAppThunk<IResProfitDetailInfo, ProfitTabType>(
  `${name}/apiGprofitGridData`,
  async (tab, thunkApi) => {
    const param = {
      year: thunkApi.getState().ui.selectbox?.["profit_detail_year"],
      month:
        thunkApi.getState().ui.selectbox?.["profit_detail_month"] === "0"
          ? null
          : thunkApi.getState().ui.selectbox?.["profit_detail_month"],
      idList: thunkApi.getState().navigates.selectChannel
        ? [thunkApi.getState().navigates.selectChannel?.value]
        : undefined,
      serviceType: tab,
    };

    const res = await Http.get<IRes<IResProfitDetailInfo>>("revenue/history", {
      params: param,
    });

    if (res.data.content) {
      thunkApi.dispatch(rdxSetProfitGridlList(res.data.content));
    }

    return res.data;
  },
);

/**
 * 수익 - 차트 데이터
 */
export const apiGProfitChartData = createAppThunk<IResProfitChartList, undefined>(
  `${name}/apiGProfitChartData`,
  async (_, thunkApi) => {
    const param = {
      year: thunkApi.getState().ui.selectbox?.["profit_year_date"],
      idList: thunkApi.getState().navigates.selectChannel
        ? [thunkApi.getState().navigates.selectChannel?.value]
        : undefined,
      // idList: thunkApi.getState().profit.selectChannelList.map(i => i.code),
      serviceType: thunkApi.getState().profit.activeTab,
    };

    const res = await Http.get<IRes<IResProfitChartList>>("revenue/chart", {
      params: param,
    });

    if (res.data.content) {
      const date = new Date();
      const year = date.getFullYear();
      if (year === Number(thunkApi.getState().ui.selectbox?.["profit_year_date"])) {
        const month = date.getMonth() + 1;
        const chart_list = res.data.content.monthly_settlement.filter(i => {
          return Number(i.date.substring(5, i.date.length)) <= month;
        });
        const data = { revenue: res.data.content.revenue, monthly_settlement: chart_list };
        thunkApi.dispatch(rdxSetChartList(data));
      } else {
        thunkApi.dispatch(rdxSetChartList(res.data.content));
      }
    }

    return res.data;
  },
);

/**
 * 수익 > 지급 > 업체별 상세 수익 데이터 엑셀 다운로드
 */
export const apiGprofitDownloadExcel = createAppThunk<IResExcelData, undefined>(
  `${name}/apiGprofitDownloadExcel`,
  async (_, thunkApi) => {
    const start_year = thunkApi.getState().ui.selectbox?.["calculate_company_popup_data_year_start"];
    const start_month = thunkApi.getState().ui.selectbox?.["calculate_company_popup_data_month_start"];
    const end_year = thunkApi.getState().ui.selectbox?.["calculate_company_popup_data_year_end"];
    const end_month = thunkApi.getState().ui.selectbox?.["calculate_company_popup_data_month_end"];

    const res = await Http.get<BlobPart>(`revenue/excel`, {
      responseType: "blob",
      // params: { date: date },
      params: {
        startDate: `${start_year}-${Number(start_month) > 10 ? start_month : `0${Number(start_month)}`}`,
        endDate: `${end_year}-${Number(end_month) > 10 ? end_month : `0${Number(end_month)}`}`,
      },
    }).then(res => {
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${start_month}월_${end_month}월_수익데이터.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return res;
    });
    if (res) {
      res.request = { ...res.request, code: res.request.status };
    }
    return res.request;
  },
);

const profitSlice = createSlice({
  name,
  initialState: {
    profitGridData: [],
    chart: {
      total_revenue: 0,
    },
    isLoading: { chart: false, profit_list: false, payment_list: false },
  } as IProfit,
  reducers: {
    rdxResetAllData(state: IProfit) {
      delete state.activeTab;
      delete state.profitDate;
      delete state.profitInfo;
    },
    rdxResetProfitChartData(state: IProfit) {
      state.chart = {};
    },

    rdxSetProfitActiveTab(state: IProfit, action: PayloadAction<ProfitTabType>) {
      state.activeTab = action.payload;
    },

    //수익 - 차트
    rdxSetChartList(state: IProfit, action: PayloadAction<IResProfitChartList>) {
      state.chart = {
        date: action.payload.monthly_settlement.map(item => moment(item.date).format("YYYY. MM")).reverse(),
        revenue: action.payload.monthly_settlement!.map(item => item.value).reverse(),
      };
    },
    //수익 - 그리드 데이터 저장
    rdxSetProfitGridlList(state: IProfit, action: PayloadAction<IResProfitDetailInfo>) {
      state.profitDate = { year: action.payload.year, month: action.payload.month };
      state.profitInfo = action.payload.contents;
    },

    //수익 - 지급 탭 그리드 데이터 저장
    rdxSetPaymentGridlList(state: IProfit, action: PayloadAction<IPaymentGridVo[]>) {
      // console.log(action.payload);
      const list = action.payload.map((item, idx) => {
        // console.log(item.detail);

        const pre_date = StringToDateCal(item.date, -2);
        const msn_pre_date = StringToDateCal(item.date, -3);
        const next_date = StringToDateCal(item.date, 2);

        return {
          id: idx,
          date: item.date,
          revenue: item.revenue,
          pre_month: pre_date,
          msn_pre_month: msn_pre_date,
          next_month: next_date,
          detail: item.detail,
        };
      });
    },
  },
  extraReducers: builder => {
    builder.addCase(apiGProfitChartData.pending, state => {
      state.isLoading.chart = true;
    });
    builder.addCase(apiGProfitChartData.fulfilled, state => {
      state.isLoading.chart = false;
    });

    builder.addCase(apiGprofitDetailList.pending, state => {
      state.isLoading.profit_list = true;
    });
    builder.addCase(apiGprofitDetailList.fulfilled, state => {
      state.isLoading.profit_list = false;
    });
  },
});

export const {
  rdxResetAllData,
  rdxResetProfitChartData,
  rdxSetProfitActiveTab,
  rdxSetPaymentGridlList,
  rdxSetChartList,
  rdxSetProfitGridlList,
} = profitSlice.actions;

export default profitSlice.reducer;
